import { useQuery } from '@apollo/client';
import { AccountStates, SubAccountStates } from '@generated/graphql';
import { useMemo } from 'react';
import { usePermission } from '../../../../../hooks/usePermission';
import {
  type DepositEligibleSubAccount,
  FETCH_DEPOSIT_ELIGIBLE_SUB_ACCOUNTS,
} from './useDepositEligibleSubAccounts.query';

export type DepositEligibleAccount = {
  account: NonNullable<DepositEligibleSubAccount['account']>;
  subAccounts: DepositEligibleSubAccount[];
};

const HIDDEN_ACCOUNT_STATES: AccountStates[] = [
  AccountStates.Inactive,
  AccountStates.Frozen,
];

/**
 * The user's accounts that have at least one deposit-eligible sub-account, in
 * query order. The server filters the sub-accounts; it can't filter on the
 * account's state, so inactive and frozen accounts are dropped here.
 */
export const useDepositEligibleSubAccounts = (userId?: string) => {
  const canReadAccountNumber = usePermission('read:account_number');
  const { data, loading } = useQuery(FETCH_DEPOSIT_ELIGIBLE_SUB_ACCOUNTS, {
    variables: {
      input: {
        filter: {
          userId: userId!,
          state: SubAccountStates.Active,
          allowClientDeposits: true,
        },
        pagination: { perPage: 1000 },
      },
      canReadAccountNumber,
    },
    skip: !userId,
  });

  const accounts = useMemo(() => {
    const visibleSubAccounts = (
      data?.fetchSubAccounts.subAccounts ?? []
    ).filter(({ account }) => !HIDDEN_ACCOUNT_STATES.includes(account.state));

    return [
      ...Map.groupBy(visibleSubAccounts, ({ account }) => account.id).values(),
    ].map(
      (subAccounts): DepositEligibleAccount => ({
        account: subAccounts[0].account,
        subAccounts,
      }),
    );
  }, [data]);

  return { accounts, loading };
};
